import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Chip,
    LinearProgress,
    Divider,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled, alpha } from '@mui/material/styles';
import { db, auth } from '../firebase';
import {
    doc,
    getDoc,
    getDocs,
    collection,
    query,
    orderBy,
    limit,
    where,
    getCountFromServer,
} from 'firebase/firestore';
import { UserContext } from '../contexts/UserContext';

// ====== THEME TOKENS (Beasts of Legend) ======
const GOLD = '#d4af37';
const GOLD_SOFT = 'rgba(212,175,55,0.75)';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';

// Shared “glass” panel styling
const panelSx = {
    background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
    color: TEXT_WARM,
    borderRadius: 2,
    border: `1px solid ${alpha(GOLD, 0.35)}`,
    boxShadow: `0 0 0 1px ${alpha(GOLD, 0.15)} inset, 0 10px 30px ${alpha(GOLD, 0.08)}`,
};

// Gold button
const GoldButton = styled(Button)(({ theme }) => ({
    fontWeight: 700,
    letterSpacing: '.6px',
    color: TEXT_WARM,
    borderRadius: 12,
    padding: '8px 14px',
    background: `linear-gradient(180deg, ${alpha(GOLD, 0.9)} 0%, ${alpha(GOLD, 0.6)} 100%)`,
    boxShadow: `0 8px 24px ${alpha(GOLD, 0.25)}`,
    textTransform: 'none',
    '&:hover': {
        boxShadow: `0 10px 28px ${alpha(GOLD, 0.35)}`,
        filter: 'brightness(1.05)',
        background: `linear-gradient(180deg, ${alpha(GOLD, 1)} 0%, ${alpha(GOLD, 0.75)} 100%)`,
    },
    '&:disabled': {
        color: alpha(TEXT_WARM, 0.6),
        background: alpha(GOLD, 0.25),
        boxShadow: 'none',
    },
}));

// Chips
const chipOutlineSx = {
    px: 1.25,
    py: 0.25,
    color: TEXT_WARM,
    borderColor: alpha(GOLD, 0.55),
    background: alpha('#000', 0.3),
    fontWeight: 600,
};

// Helpers
const fmt = (n) => Number(n ?? 0).toLocaleString();

const formatCountdown = (endMs, nowMs) => {
    if (!endMs) return '—';
    const diff = Math.max(0, endMs - nowMs);
    const totalSec = Math.floor(diff / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    const pad = (x) => String(x).padStart(2, '0');

    if (days > 0) {
        return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
    }
    if (hours > 0) {
        return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
    return `${pad(minutes)}m ${pad(seconds)}s`;
};

// Static win milestones / rewards (UI only; real rewards come from Cloud Functions config)
const FIXED_WIN_MILESTONES = [
    {
        threshold: 25,
        label: '25 Wins',
        description: '1 Rare+ Draw Ticket',
        ticketType: 'rareplus',
    },
    {
        threshold: 50,
        label: '50 Wins',
        description: '1 Rare+ Draw Ticket',
        ticketType: 'rareplus',
    },
    {
        threshold: 100,
        label: '100 Wins',
        description: '1 High Rare+ Draw Ticket',
        ticketType: 'highrareplus',
    },
];

const REPEATING_WIN_RULES = [
    {
        label: 'Every 5 Wins',
        description: 'Soul Dust (amount defined server-side)',
    },
    {
        label: 'Every 10 Wins',
        description: '1 Basic Draw Ticket (except at 50 & 100 wins)',
    },
];

const RANK_REWARD_TIERS = [
    {
        label: '#1',
        range: 'Rank 1',
        description: '1 Super Rare+ Draw Ticket',
    },
    {
        label: 'Top 10',
        range: 'Ranks 2–10',
        description: '3 High Rare+ Draw Tickets',
    },
    {
        label: 'Top 25',
        range: 'Ranks 11–25',
        description: '2 High Rare+ Draw Tickets',
    },
    {
        label: 'Top 50',
        range: 'Ranks 26–50',
        description: '1 High Rare+ Draw Ticket',
    },
    {
        label: 'Top 1,000',
        range: 'Ranks 51–1,000',
        description: '1 Rare+ Draw Ticket',
    },
    {
        label: 'Top 5,000',
        range: 'Ranks 1,001–5,000',
        description: '1 Basic Draw Ticket',
    },
    {
        label: 'Everyone Else',
        range: 'Below Rank 5,000',
        description: '10 Soul Dust',
    },
];

export default function ArenaWinsEvent() {
    const { userData } = useContext(UserContext) || {};
    const [open, setOpen] = useState(false);

    const [eventDoc, setEventDoc] = useState(null);
    const [eventLoading, setEventLoading] = useState(false);

    const [participantDoc, setParticipantDoc] = useState(null);
    const [participantLoading, setParticipantLoading] = useState(false);

    const [error, setError] = useState(null);
    const [nowMs, setNowMs] = useState(Date.now());

    // Last event (personal summary + leaderboard)
    const [lastEventSummary, setLastEventSummary] = useState(null);

    // Current event leaderboard (Top 100 snapshot)
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [leaderboardError, setLeaderboardError] = useState(null);

    // Full event rank (even outside Top 100)
    const [userRankFull, setUserRankFull] = useState(null);
    const [userRankLoading, setUserRankLoading] = useState(false);
    const [userRankError, setUserRankError] = useState(null);

    // Simple refetch trigger to avoid listeners
    const [refreshKey, setRefreshKey] = useState(0);

    const uid = auth?.currentUser?.uid || userData?.uid || null;

    const lastEventMeta = userData?.arenaLastEvent || null;
    const lastEventId = lastEventMeta?.eventId || null;

    // Tick every second while dialog is open (for countdown only – no DB)
    useEffect(() => {
        if (!open) return;
        const id = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(id);
    }, [open]);

    // One-shot load of event + participant + leaderboard + lastEventSummary
    useEffect(() => {
        if (!open) return;

        let cancelled = false;

        const loadAll = async () => {
            setEventLoading(true);
            setError(null);

            if (uid) {
                setParticipantLoading(true);
            } else {
                setParticipantLoading(false);
                setParticipantDoc(null);
            }

            setLeaderboardLoading(true);
            setLeaderboardError(null);
            setUserRankFull(null);
            setUserRankError(null);

            if (!lastEventId) {
                setLastEventSummary(null);
            }

            try {
                const eventRef = doc(db, 'arenaEvents', 'current');
                const eventSnap = await getDoc(eventRef);

                if (cancelled) return;

                if (!eventSnap.exists()) {
                    setEventDoc(null);
                    setEventLoading(false);
                    setParticipantDoc(null);
                    setParticipantLoading(false);
                    setLeaderboard([]);
                    setLeaderboardLoading(false);

                    // still can load last event summary if exists
                    if (lastEventId) {
                        try {
                            const historyRef = doc(db, 'arenaEventsHistory', lastEventId);
                            const historySnap = await getDoc(historyRef);
                            if (!cancelled) {
                                setLastEventSummary(
                                    historySnap.exists()
                                        ? { id: historySnap.id, ...historySnap.data() }
                                        : null
                                );
                            }
                        } catch (e) {
                            console.error(
                                '[ArenaWinsEvent] last event history load error:',
                                e
                            );
                        }
                    }

                    return;
                }

                const eventData = { id: eventSnap.id, ...(eventSnap.data() || {}) };
                setEventDoc(eventData);
                setEventLoading(false);

                const promises = [];

                // Participant doc (user’s event progress)
                if (uid) {
                    const participantRef = doc(collection(eventRef, 'participants'), uid);
                    promises.push(
                        getDoc(participantRef)
                            .then((snap) => {
                                if (cancelled) return;
                                if (snap.exists()) {
                                    setParticipantDoc({
                                        id: snap.id,
                                        ...(snap.data() || {}),
                                    });
                                } else {
                                    setParticipantDoc(null);
                                }
                                setParticipantLoading(false);
                            })
                            .catch((err) => {
                                if (cancelled) return;
                                console.error(
                                    '[ArenaWinsEvent] participant load error:',
                                    err
                                );
                                setParticipantDoc(null);
                                setParticipantLoading(false);
                                setError('Failed to load your event progress.');
                            })
                    );
                }

                // Top 100 leaderboard for current event
                const participantsRef = collection(eventRef, 'participants');
                const lbQuery = query(
                    participantsRef,
                    orderBy('wins', 'desc'),
                    orderBy('lastWinAt', 'asc'),
                    limit(100)
                );

                promises.push(
                    getDocs(lbQuery)
                        .then((snap) => {
                            if (cancelled) return;
                            const rows = snap.docs.map((d) => ({
                                id: d.id,
                                ...(d.data() || {}),
                            }));
                            setLeaderboard(rows);
                            setLeaderboardLoading(false);
                        })
                        .catch((err) => {
                            if (cancelled) return;
                            console.error(
                                '[ArenaWinsEvent] leaderboard load error:',
                                err
                            );
                            setLeaderboard([]);
                            setLeaderboardLoading(false);
                            setLeaderboardError('Failed to load leaderboard.');
                        })
                );

                // Last event history (top ranks snapshot)
                if (lastEventId) {
                    const historyRef = doc(db, 'arenaEventsHistory', lastEventId);
                    promises.push(
                        getDoc(historyRef)
                            .then((snap) => {
                                if (cancelled) return;
                                if (!snap.exists()) {
                                    setLastEventSummary(null);
                                } else {
                                    setLastEventSummary({
                                        id: snap.id,
                                        ...(snap.data() || {}),
                                    });
                                }
                            })
                            .catch((err) => {
                                if (cancelled) return;
                                console.error(
                                    '[ArenaWinsEvent] last event history load error:',
                                    err
                                );
                            })
                    );
                }

                await Promise.all(promises);
            } catch (err) {
                if (cancelled) return;
                console.error('[ArenaWinsEvent] general load error:', err);
                setError('Failed to load event info.');
                setEventDoc(null);
                setEventLoading(false);
                setParticipantDoc(null);
                setParticipantLoading(false);
                setLeaderboard([]);
                setLeaderboardLoading(false);
                setLeaderboardError('Failed to load leaderboard.');
            }
        };

        loadAll();

        return () => {
            cancelled = true;
        };
    }, [open, uid, lastEventId, refreshKey]);

    const wins = Number(participantDoc?.wins || 0);

    const endAtMs = useMemo(() => {
        const raw = eventDoc?.endAt;
        if (!raw) return null;
        if (typeof raw.toMillis === 'function') return raw.toMillis();
        if (typeof raw === 'number') return raw;
        return null;
    }, [eventDoc]);

    const startAtMs = useMemo(() => {
        const raw = eventDoc?.startAt;
        if (!raw) return null;
        if (typeof raw.toMillis === 'function') return raw.toMillis();
        if (typeof raw === 'number') return raw;
        return null;
    }, [eventDoc]);

    const isActive = useMemo(() => {
        if (!eventDoc || !endAtMs) return false;
        const now = nowMs;
        const hasStarted = !startAtMs || startAtMs <= now;
        const notEnded = endAtMs > now;
        const statusField = eventDoc.status || 'active';
        return hasStarted && notEnded && statusField === 'active';
    }, [eventDoc, endAtMs, startAtMs, nowMs]);

    const countdownLabel = useMemo(() => {
        if (!eventDoc || !endAtMs) return 'No active event';
        if (!isActive) return 'Event ended';
        return formatCountdown(endAtMs, nowMs);
    }, [eventDoc, endAtMs, nowMs, isActive]);

    const progressPct = useMemo(() => {
        if (!wins) return 0;
        const cap = 100;
        return Math.min(100, (wins / cap) * 100);
    }, [wins]);

    const nextMilestone = useMemo(() => {
        const thresholds = FIXED_WIN_MILESTONES.map((m) => m.threshold).sort(
            (a, b) => a - b
        );
        const next = thresholds.find((t) => wins < t);
        if (!next) return null;
        return FIXED_WIN_MILESTONES.find((m) => m.threshold === next) || null;
    }, [wins]);

    // Compute user's full event rank (even outside Top 100)
    useEffect(() => {
        if (!open) {
            setUserRankFull(null);
            setUserRankError(null);
            return;
        }
        if (!uid || !participantDoc || !eventDoc) {
            setUserRankFull(null);
            setUserRankError(null);
            return;
        }

        const userWins = Number(participantDoc.wins || 0);
        const userLastWinAt = participantDoc.lastWinAt;

        // If no wins yet or no lastWinAt, we can't rank meaningfully
        if (!Number.isFinite(userWins) || userWins <= 0 || !userLastWinAt) {
            setUserRankFull(null);
            setUserRankError(null);
            return;
        }

        let cancelled = false;
        const run = async () => {
            setUserRankLoading(true);
            setUserRankError(null);

            try {
                const eventRef = doc(db, 'arenaEvents', 'current');
                const participantsRef = collection(eventRef, 'participants');

                // Count players with more wins
                const qGreaterWins = query(
                    participantsRef,
                    where('wins', '>', userWins)
                );

                // Count players with equal wins but earlier lastWinAt
                const qEqualEarlier = query(
                    participantsRef,
                    where('wins', '==', userWins),
                    where('lastWinAt', '<', userLastWinAt)
                );

                const [greaterSnap, equalSnap] = await Promise.all([
                    getCountFromServer(qGreaterWins),
                    getCountFromServer(qEqualEarlier),
                ]);

                if (cancelled) return;

                const above =
                    Number(greaterSnap.data().count || 0) +
                    Number(equalSnap.data().count || 0);

                const rank = above + 1;
                setUserRankFull(rank);
                setUserRankLoading(false);
            } catch (e) {
                if (cancelled) return;
                console.error('[ArenaWinsEvent] rank calculation error:', e);
                setUserRankLoading(false);
                setUserRankFull(null);
                setUserRankError('Failed to calculate your rank.');
            }
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [open, uid, participantDoc, eventDoc, refreshKey]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setError(null);
    };

    const handleRefresh = () => {
        // Trigger the effect to re-run without a persistent listener
        setRefreshKey((k) => k + 1);
    };

    const lastTop = useMemo(() => {
        if (!lastEventSummary || !Array.isArray(lastEventSummary.top)) return [];
        return [...lastEventSummary.top].sort(
            (a, b) => (a.rank || 0) - (b.rank || 0)
        );
    }, [lastEventSummary]);

    return (
        <>
            {/* Trigger button (wrapped in Tooltip in Arena.js) */}
            <GoldButton onClick={handleOpen} size="small">
                Arena Wins Event
            </GoldButton>

            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        ...panelSx,
                        borderRadius: 2,
                        border: `1px solid ${alpha(GOLD, 0.4)}`,
                        boxShadow: `0 0 0 1px ${alpha(
                            GOLD,
                            0.2
                        )} inset, 0 18px 40px ${alpha(GOLD, 0.25)}`,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        color: TEXT_WARM,
                        fontFamily: `'Cinzel', serif`,
                        letterSpacing: '.8px',
                        borderBottom: `1px solid ${alpha(GOLD, 0.3)}`,
                        pb: 1,
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: `'Cinzel', serif`,
                                letterSpacing: '.9px',
                            }}
                        >
                            Monthly Arena Wins Event
                        </Typography>
                        <Chip
                            size="small"
                            label={
                                eventLoading
                                    ? 'Loading…'
                                    : !eventDoc
                                    ? 'No Event'
                                    : isActive
                                    ? 'Active'
                                    : 'Ended'
                            }
                            sx={{
                                ...chipOutlineSx,
                                fontSize: 11,
                            }}
                        />
                    </Stack>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon sx={{ color: GOLD }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 2, pb: 1.5 }}>
                    {/* Top summary row: countdown + explanation */}
                    <Stack spacing={2}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            justifyContent="space-between"
                        >
                            <Stack spacing={0.75}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: alpha(TEXT_WARM, 0.95) }}
                                >
                                    Climb the Arena by winning battles this month. Each win counts
                                    towards event milestones and your final ranking.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: alpha(TEXT_WARM, 0.9) }}
                                >
                                    <Box component="span" sx={{ color: GOLD }}>
                                        Rewards are not granted automatically.
                                    </Box>{' '}
                                    When you hit a milestone or receive a rank reward, it will
                                    appear as an in-game notification. Open your notifications and
                                    tap{' '}
                                    <Box component="span" sx={{ fontStyle: 'italic' }}>
                                        Claim
                                    </Box>{' '}
                                    to receive the draw tickets or soul dust.
                                </Typography>
                            </Stack>

                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(GOLD, 0.4)}`,
                                    px: 1.5,
                                    py: 1,
                                    minWidth: 200,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ color: GOLD_DIM, display: 'block', mb: 0.25 }}
                                >
                                    Time Remaining
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: TEXT_WARM,
                                        fontFamily: `'Cinzel', serif`,
                                        letterSpacing: '.8px',
                                    }}
                                >
                                    {countdownLabel}
                                </Typography>
                            </Box>
                        </Stack>

                        <Divider sx={{ borderColor: alpha(GOLD, 0.3) }} />

                        {/* Player progress */}
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${alpha(GOLD, 0.4)}`,
                                p: 1.5,
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent="space-between"
                            >
                                <Stack spacing={0.4}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: GOLD_DIM,
                                            textTransform: 'uppercase',
                                        }}
                                    >
                                        Your Event Progress
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: TEXT_WARM,
                                            fontFamily: `'Cinzel', serif`,
                                            letterSpacing: '.7px',
                                        }}
                                    >
                                        Total Arena Wins this event:{' '}
                                        <Box component="span" sx={{ color: GOLD }}>
                                            {participantLoading ? '—' : fmt(wins)}
                                        </Box>
                                    </Typography>
                                    {nextMilestone ? (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: alpha(TEXT_WARM, 0.9) }}
                                        >
                                            Next major milestone:{' '}
                                            <Box component="span" sx={{ color: GOLD }}>
                                                {nextMilestone.label}
                                            </Box>{' '}
                                            ({nextMilestone.description})
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            sx={{ color: alpha(TEXT_WARM, 0.9) }}
                                        >
                                            You&apos;ve reached all listed fixed milestones for
                                            this event. Keep winning for rank rewards!
                                        </Typography>
                                    )}
                                </Stack>

                                <Box sx={{ flexGrow: 1, maxWidth: 260 }}>
                                    <Stack spacing={0.5}>
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{ color: GOLD_DIM }}
                                            >
                                                0 wins
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: GOLD_DIM }}
                                            >
                                                100 wins
                                            </Typography>
                                        </Stack>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progressPct}
                                            sx={{
                                                height: 8,
                                                borderRadius: 999,
                                                backgroundColor: alpha(
                                                    '#ffffff',
                                                    0.06
                                                ),
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 999,
                                                    background: `linear-gradient(90deg, ${GOLD_SOFT}, ${GOLD})`,
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>

                        <Divider sx={{ borderColor: alpha(GOLD, 0.3) }} />

                        {/* Win-based rewards */}
                        <Stack spacing={1.5}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: TEXT_WARM,
                                    fontFamily: `'Cinzel', serif`,
                                    letterSpacing: '.7px',
                                }}
                            >
                                Win-Based Rewards
                            </Typography>

                            {/* Fixed milestones */}
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {FIXED_WIN_MILESTONES.map((m) => {
                                    const reached = wins >= m.threshold;
                                    return (
                                        <Box
                                            key={m.threshold}
                                            sx={{
                                                borderRadius: 2,
                                                border: `1px solid ${
                                                    reached ? GOLD : alpha(GOLD, 0.4)
                                                }`,
                                                backgroundColor: reached
                                                    ? alpha(GOLD, 0.16)
                                                    : alpha('#000', 0.4),
                                                px: 1.5,
                                                py: 1,
                                                minWidth: 190,
                                            }}
                                        >
                                            <Stack spacing={0.3}>
                                                <Stack
                                                    direction="row"
                                                    spacing={0.75}
                                                    alignItems="center"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: TEXT_WARM }}
                                                    >
                                                        {m.label}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            reached
                                                                ? 'Reached'
                                                                : 'Upcoming'
                                                        }
                                                        sx={{
                                                            ...chipOutlineSx,
                                                            borderColor: reached
                                                                ? GOLD
                                                                : alpha(GOLD, 0.4),
                                                            backgroundColor: reached
                                                                ? alpha(GOLD, 0.15)
                                                                : chipOutlineSx.background,
                                                            fontSize: 11,
                                                            px: 0.75,
                                                        }}
                                                    />
                                                </Stack>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: GOLD_DIM }}
                                                >
                                                    {m.description}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: alpha(TEXT_WARM, 0.85),
                                                    }}
                                                >
                                                    Reward will appear as a
                                                    notification. Claim it to
                                                    receive the draw ticket.
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Stack>

                            {/* Repeating rules */}
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(GOLD, 0.35)}`,
                                    px: 1.5,
                                    py: 1.25,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: GOLD_DIM,
                                        textTransform: 'uppercase',
                                        letterSpacing: '.7px',
                                        display: 'block',
                                        mb: 0.5,
                                    }}
                                >
                                    Ongoing Rewards
                                </Typography>
                                <Stack spacing={0.3}>
                                    {REPEATING_WIN_RULES.map((r) => (
                                        <Typography
                                            key={r.label}
                                            variant="body2"
                                            sx={{
                                                color: alpha(TEXT_WARM, 0.9),
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{ color: GOLD }}
                                            >
                                                {r.label}:
                                            </Box>{' '}
                                            {r.description}
                                        </Typography>
                                    ))}
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: alpha(TEXT_WARM, 0.85),
                                            mt: 0.5,
                                            display: 'block',
                                        }}
                                    >
                                        These rewards also arrive as notifications.
                                        Claim them to convert them into tickets or
                                        soul dust.
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>

                        <Divider sx={{ borderColor: alpha(GOLD, 0.3) }} />

                        {/* Rank-based rewards */}
                        <Stack spacing={1.5}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: TEXT_WARM,
                                    fontFamily: `'Cinzel', serif`,
                                    letterSpacing: '.7px',
                                }}
                            >
                                End-of-Event Rank Rewards
                            </Typography>

                            <Typography
                                variant="body2"
                                sx={{ color: alpha(TEXT_WARM, 0.9) }}
                            >
                                At the end of the month, all participants are
                                ranked by total event wins. Rewards are issued once
                                and must be claimed from your notifications.
                            </Typography>

                            <Stack spacing={1}>
                                {RANK_REWARD_TIERS.map((tier) => (
                                    <Box
                                        key={tier.label}
                                        sx={{
                                            borderRadius: 2,
                                            border: `1px solid ${alpha(GOLD, 0.35)}`,
                                            px: 1.5,
                                            py: 1,
                                            background: alpha('#000', 0.4),
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            spacing={2}
                                        >
                                            <Stack spacing={0.2}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: TEXT_WARM,
                                                        fontFamily: `'Cinzel', serif`,
                                                    }}
                                                >
                                                    {tier.label}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: GOLD_DIM }}
                                                >
                                                    {tier.range}
                                                </Typography>
                                            </Stack>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: alpha(TEXT_WARM, 0.95),
                                                }}
                                            >
                                                {tier.description}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Stack>

                        <Divider sx={{ borderColor: alpha(GOLD, 0.3) }} />

                        {/* Current Event Rank + Leaderboard (Top 100 snapshot) */}
                        <Stack spacing={1.5}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: TEXT_WARM,
                                    fontFamily: `'Cinzel', serif`,
                                    letterSpacing: '.7px',
                                }}
                            >
                                Current Event Leaderboard – Top 100
                            </Typography>

                            {/* Always show user rank for the whole event */}
                            {uid && (
                                <Typography
                                    variant="body2"
                                    sx={{ color: alpha(TEXT_WARM, 0.9) }}
                                >
                                    Your current placement this event:{' '}
                                    <Box component="span" sx={{ color: GOLD }}>
                                        {participantLoading || userRankLoading
                                            ? 'Calculating…'
                                            : !participantDoc
                                            ? 'Not ranked yet'
                                            : userRankError
                                            ? 'Unavailable'
                                            : userRankFull
                                            ? `#${fmt(userRankFull)}${
                                                  userRankFull > 100
                                                      ? ' (outside Top 100)'
                                                      : ''
                                              }`
                                            : 'Not ranked yet'}
                                    </Box>
                                </Typography>
                            )}

                            <Box
                                sx={{
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(GOLD, 0.4)}`,
                                    p: 1.25,
                                    maxHeight: 260,
                                    overflowY: 'auto',
                                    background: alpha('#000', 0.35),
                                }}
                            >
                                {leaderboardLoading ? (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: alpha(TEXT_WARM, 0.9) }}
                                    >
                                        Loading leaderboard…
                                    </Typography>
                                ) : leaderboardError ? (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: alpha('#ff6b6b', 0.9) }}
                                    >
                                        {leaderboardError}
                                    </Typography>
                                ) : leaderboard.length === 0 ? (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: alpha(TEXT_WARM, 0.85) }}
                                    >
                                        No wins recorded yet. Win your first battle
                                        to appear on the board!
                                    </Typography>
                                ) : (
                                    <Stack spacing={0.5}>
                                        {leaderboard.map((row, idx) => {
                                            const isYou =
                                                uid &&
                                                (row.userId || row.id) === uid;
                                            return (
                                                <Box
                                                    key={row.userId || row.id}
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        px: 1,
                                                        py: 0.5,
                                                        border: `1px solid ${
                                                            isYou
                                                                ? GOLD
                                                                : alpha(
                                                                      GOLD,
                                                                      0.25
                                                                  )
                                                        }`,
                                                        background: isYou
                                                            ? alpha(GOLD, 0.15)
                                                            : 'transparent',
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        spacing={1.5}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            alignItems="center"
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: isYou
                                                                        ? GOLD
                                                                        : TEXT_WARM,
                                                                }}
                                                            >
                                                                #{fmt(idx + 1)}
                                                            </Typography>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    color: TEXT_WARM,
                                                                }}
                                                            >
                                                                {isYou
                                                                    ? 'You'
                                                                    : row.username ||
                                                                      row.displayName ||
                                                                      'Unknown Challenger'}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: alpha(
                                                                    TEXT_WARM,
                                                                    0.9
                                                                ),
                                                            }}
                                                        >
                                                            {fmt(row.wins || 0)}{' '}
                                                            wins
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>
                        </Stack>

                        <Divider sx={{ borderColor: alpha(GOLD, 0.3) }} />

                        {/* Previous Event Results */}
                        <Stack spacing={1.5}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: TEXT_WARM,
                                    fontFamily: `'Cinzel', serif`,
                                    letterSpacing: '.7px',
                                }}
                            >
                                Previous Event Results
                            </Typography>

                            {lastEventMeta ? (
                                <Box
                                    sx={{
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(GOLD, 0.4)}`,
                                        p: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: alpha(TEXT_WARM, 0.95),
                                            mb: 0.5,
                                        }}
                                    >
                                        Your last event:{' '}
                                        <Box
                                            component="span"
                                            sx={{ color: GOLD }}
                                        >
                                            {lastEventMeta.eventId ||
                                                'Unknown Event'}
                                        </Box>
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: alpha(TEXT_WARM, 0.9),
                                        }}
                                    >
                                        Final rank:{' '}
                                        <Box
                                            component="span"
                                            sx={{ color: GOLD }}
                                        >
                                            {lastEventMeta.rank
                                                ? `#${fmt(lastEventMeta.rank)}`
                                                : '—'}
                                        </Box>{' '}
                                        with{' '}
                                        <Box
                                            component="span"
                                            sx={{ color: GOLD }}
                                        >
                                            {fmt(lastEventMeta.wins || 0)} wins
                                        </Box>
                                        .
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{ color: alpha(TEXT_WARM, 0.85) }}
                                >
                                    You don&apos;t have any previous event
                                    results yet. Join the Monthly Arena Wins
                                    event and start climbing!
                                </Typography>
                            )}

                            {lastTop.length > 0 && (
                                <Box
                                    sx={{
                                        borderRadius: 2,
                                        border: `1px solid ${alpha(GOLD, 0.4)}`,
                                        p: 1.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: GOLD_DIM,
                                            textTransform: 'uppercase',
                                            letterSpacing: '.7px',
                                            display: 'block',
                                            mb: 0.75,
                                        }}
                                    >
                                        Top Players – Last Event
                                    </Typography>
                                    <Stack spacing={0.75}>
                                        {lastTop.map((row) => (
                                            <Stack
                                                key={row.userId}
                                                direction="row"
                                                spacing={1.5}
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ color: GOLD }}
                                                    >
                                                        #{fmt(row.rank || 0)}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: TEXT_WARM,
                                                        }}
                                                    >
                                                        {row.username ||
                                                            'Unknown Champion'}
                                                    </Typography>
                                                </Stack>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: alpha(
                                                            TEXT_WARM,
                                                            0.9
                                                        ),
                                                    }}
                                                >
                                                    {fmt(row.wins || 0)} wins
                                                </Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </Stack>

                        {error && (
                            <Typography
                                variant="caption"
                                sx={{
                                    mt: 0.5,
                                    color: alpha('#ff6b6b', 0.9),
                                }}
                            >
                                {error}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        borderTop: `1px solid ${alpha(GOLD, 0.3)}`,
                        px: 2,
                        py: 1.25,
                        justifyContent: 'space-between',
                    }}
                >
                    <Button
                        onClick={handleRefresh}
                        sx={{
                            color: TEXT_WARM,
                            textTransform: 'none',
                        }}
                    >
                        Refresh
                    </Button>
                    <Button
                        onClick={handleClose}
                        sx={{
                            color: TEXT_WARM,
                            textTransform: 'none',
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
